import React, { useEffect, useRef } from "react";
import { usePublicSettings } from "@/lib/publicSettings";

// Cloudflare Turnstile widget.
//   - Renders a hidden invisible/managed challenge when a site key is configured.
//   - Emits token via `onToken` prop. Emits "" on expire/error.
//   - If no site key present in public settings, renders NOTHING and calls onToken("BYPASS")
//     so callers can proceed (dev/local mode) without breaking flows.
let scriptLoaded = false;
let scriptLoading = null;

const loadScript = () => {
  if (scriptLoaded) return Promise.resolve();
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => { scriptLoaded = true; resolve(); };
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptLoading;
};

const Turnstile = ({ onToken, action = "generic", size = "flexible", theme = "light" }) => {
  const { settings } = usePublicSettings();
  const ref = useRef(null);
  const widgetIdRef = useRef(null);

  const siteKey = settings?.turnstile?.site_key || "";

  useEffect(() => {
    if (!siteKey) {
      // Dev fallback — bypass token for the backend, which will accept when no secret is configured.
      onToken?.("BYPASS-DEV");
      return () => {};
    }
    let cancelled = false;
    (async () => {
      try {
        await loadScript();
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          action,
          size,
          theme,
          callback: (t) => onToken?.(t),
          "expired-callback": () => onToken?.(""),
          "error-callback": () => onToken?.(""),
        });
      } catch (e) {
        // Fall back to bypass so users can still submit if Turnstile fails to load.
        console.warn("Turnstile load failed", e);
        onToken?.("BYPASS-DEV");
      }
    })();
    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (e) { /* ignore */ if (typeof console !== "undefined") console.debug("[silent]", e); }
    };
  }, [siteKey, action, size, theme, onToken]);

  if (!siteKey) return null;

  return <div ref={ref} data-testid="turnstile-widget" className="mt-4" />;
};

export default Turnstile;
